import { automationService } from '@/services/automation.service';
import type {
  DsnAcceptanceSummary,
  DsnReturn,
  DsnReturnArtifact,
} from '@/types/dsn-return.types';
import {
  AlertTriangle,
  ChevronDown,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DsnReturnDetailsProps {
  executionUuid: string;
  summary: DsnAcceptanceSummary;
}

const KIND_FALLBACKS: Record<DsnReturnArtifact['kind'], string> = {
  cco: 'Certificat de conformité',
  ban: "Bilan d'anomalies",
  crm: 'Compte rendu métier',
  bis: "Bilan d'identification",
};

export function DsnReturnDetails({
  executionUuid,
  summary,
}: DsnReturnDetailsProps) {
  const { t } = useTranslation();
  const [returns, setReturns] = useState<DsnReturn[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Un retour à traiter se consulte même sans fichier à télécharger : les
  // pièces en anomalie (lues dans la console) se listent ici.
  const actionable =
    summary.status === 'rejected' ||
    summary.status === 'accepted_with_anomalies';
  if (summary.availableReports <= 0 && !actionable) return null;

  const load = async () => {
    if (loading || returns) return;
    setLoading(true);
    setLoadError(false);
    try {
      setReturns(await automationService.getDsnReturns(executionUuid));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const download = async (
    dsnReturn: DsnReturn,
    artifact: DsnReturnArtifact
  ) => {
    setDownloading(artifact.uuid);
    setDownloadError(false);
    try {
      const blob = await automationService.downloadDsnReturnArtifact(
        executionUuid,
        dsnReturn.uuid,
        artifact.uuid
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = artifact.filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(null);
    }
  };

  const actionLabel = actionable
    ? t(
        'automation.dsnReturn.details.actionRequired',
        'Voir le retour à traiter'
      )
    : summary.availableReports === 1
      ? t('automation.dsnReturn.details.single', 'Voir le justificatif')
      : t('automation.dsnReturn.details.multiple', {
          defaultValue: 'Voir les {{count}} retours',
          count: summary.availableReports,
        });

  return (
    <details
      className="group/returns mt-3"
      onToggle={event => {
        if (event.currentTarget.open) void load();
      }}
    >
      <summary className="text-primary-700 hover:text-primary-800 focus-visible:ring-primary-500 inline-flex cursor-pointer list-none items-center gap-1.5 rounded-md text-sm font-medium underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none">
        <FileText aria-hidden="true" className="h-4 w-4" />
        {actionLabel}
        <ChevronDown
          aria-hidden="true"
          className="h-4 w-4 transition-transform group-open/returns:rotate-180"
        />
      </summary>

      <div className="mt-2 border-t border-neutral-100 pt-2" aria-live="polite">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-neutral-600">
            <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            {t(
              'automation.dsnReturn.details.loading',
              'Chargement des retours…'
            )}
          </p>
        )}
        {loadError && (
          <div role="alert" className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-neutral-700">
              {t(
                'automation.dsnReturn.details.unavailable',
                'Le document est momentanément indisponible.'
              )}
            </p>
            <button
              type="button"
              className="text-primary-700 hover:text-primary-800 focus-visible:ring-primary-500 rounded text-sm font-medium underline underline-offset-4 focus-visible:ring-2 focus-visible:outline-none"
              onClick={() => {
                setReturns(null);
                void load();
              }}
            >
              {t('automation.dsnReturn.details.retry', 'Réessayer')}
            </button>
          </div>
        )}
        {downloadError && !loadError && (
          <p role="alert" className="text-sm text-neutral-700">
            {t(
              'automation.dsnReturn.details.downloadUnavailable',
              'Le téléchargement a échoué. Vous pouvez réessayer.'
            )}
          </p>
        )}
        {returns?.flatMap(dsnReturn =>
          (dsnReturn.piecesEnAnomalie ?? []).map(piece => (
            <div
              key={piece.uuid}
              className="flex min-h-10 w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm text-neutral-700"
            >
              <span className="min-w-0">
                <span className="block font-medium">
                  {piece.libelle}
                  <span className="font-normal text-neutral-500">
                    {' · '}
                    {piece.categorie}
                  </span>
                </span>
                <span className="block truncate text-xs text-neutral-500">
                  {[piece.statut, piece.salarie].filter(Boolean).join(' · ')}
                </span>
              </span>
              <AlertTriangle
                aria-label={t(
                  'automation.dsnReturn.details.pieceAnomaly',
                  'Compte rendu en anomalie'
                )}
                className="text-warning-600 h-4 w-4 shrink-0"
              />
            </div>
          ))
        )}
        {returns &&
          actionable &&
          returns.every(
            dsnReturn => (dsnReturn.piecesEnAnomalie ?? []).length === 0
          ) &&
          returns.every(dsnReturn => dsnReturn.artifacts.length === 0) && (
            <p className="text-sm text-neutral-600">
              {t(
                'automation.dsnReturn.details.noPieceYet',
                'Le détail des comptes rendus n’a pas encore été lu.'
              )}
            </p>
          )}
        {returns?.flatMap(dsnReturn =>
          dsnReturn.artifacts.map(artifact => (
            <button
              key={artifact.uuid}
              type="button"
              disabled={downloading === artifact.uuid}
              onClick={() => void download(dsnReturn, artifact)}
              className="hover:bg-primary-50 focus-visible:ring-primary-500 flex min-h-10 w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm text-neutral-700 transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block font-medium">
                  {t(
                    `automation.dsnReturn.details.${artifact.kind}`,
                    KIND_FALLBACKS[artifact.kind]
                  )}
                </span>
                <span className="block truncate text-xs text-neutral-500">
                  {artifact.filename}
                </span>
              </span>
              {downloading === artifact.uuid ? (
                <Loader2
                  aria-label={t(
                    'automation.dsnReturn.details.downloading',
                    'Téléchargement en cours'
                  )}
                  className="h-4 w-4 shrink-0 animate-spin"
                />
              ) : (
                <Download aria-hidden="true" className="h-4 w-4 shrink-0" />
              )}
            </button>
          ))
        )}
      </div>
    </details>
  );
}
